import confetti from "canvas-confetti";
import { Component, type ErrorInfo, type PointerEvent, type ReactNode } from "react";
import { BoardGrid } from "./BoardGrid";
import { HallScene } from "./HallScene";
import { MixFallback } from "./MixFallback";
import { COLUMN_COLORS, COLUMN_INK, columnFor } from "./logic";
import { selectPageLiteMode, useHallStore } from "./store";
import { hallTheme, type HallThemeId } from "./themes";

class SceneErrorBoundary extends Component<
  { fallback: ReactNode; onFail: () => void; children?: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(_error: Error, _info: ErrorInfo) {
    this.props.onFail();
  }
  render() {
    if (this.state.failed) return this.props.fallback;
    return this.props.children;
  }
}

export function fireHallBingoConfetti(themeId: HallThemeId) {
  if (selectPageLiteMode(useHallStore.getState())) return;
  const marks = hallTheme(themeId).marks;
  const shapes = marks.map((mark) => confetti.shapeFromText({ text: mark, scalar: 2.2 }));
  const burst = (originY: number, count: number) => {
    confetti({
      particleCount: count,
      spread: 110,
      startVelocity: 42,
      origin: { y: originY },
      shapes,
      scalar: 2.2,
      ticks: 220,
      gravity: 0.85,
      zIndex: 80,
    });
  };
  burst(0.62, 90);
  window.setTimeout(() => burst(0.48, 70), 260);
  window.setTimeout(() => burst(0.7, 50), 520);
}

type HallBoardProps = {
  sceneOn: boolean;
  onSceneFail: () => void;
  onSurfacePointer?: (e: PointerEvent<HTMLDivElement>) => void;
  className?: string;
};

export function HallBoard({ sceneOn, onSceneFail, onSurfacePointer, className = "" }: HallBoardProps) {
  const phase = useHallStore((s) => s.phase);
  const current = useHallStore((s) => s.current);
  const liteMode = useHallStore(selectPageLiteMode);
  const liveScene = sceneOn && !liteMode;
  const overlaying = phase === "mixing" || phase === "dropping" || phase === "calling";
  const mixVisual = phase === "mixing" || phase === "dropping";
  const letter = current != null ? columnFor(current) : null;

  return (
    <div className={`relative h-full min-h-0 w-full ${className}`} onPointerUp={onSurfacePointer}>
      <div className="relative h-full min-h-0 w-full">
        <BoardGrid />
        {overlaying ? (
          <div className={`pointer-events-none absolute inset-0 z-[15] transition-colors duration-300 ${phase === "calling" ? "bg-hall/50" : "bg-hall/30"}`} />
        ) : null}
        {liveScene ? (
          <div className="v2-stage pointer-events-none absolute inset-0 z-20">
            <SceneErrorBoundary fallback={null} onFail={onSceneFail}>
              <HallScene
                onLost={() => {
                  if (selectPageLiteMode(useHallStore.getState())) return;
                  onSceneFail();
                }}
              />
            </SceneErrorBoundary>
          </div>
        ) : null}
        {mixVisual && !liveScene ? <MixFallback /> : null}
      </div>

      {phase === "calling" && current != null && liveScene ? (
        <div className="pointer-events-none absolute inset-x-0 top-2 z-[25] flex justify-center px-3 md:top-3 md:px-4">
          <div className="v2-callout flex items-center gap-2.5 rounded-full border border-hall-line bg-hall-panel/92 px-3 py-1.5 md:gap-3 md:px-5 md:py-2">
            <span className="font-sans text-[0.6rem] tracking-[0.28em] text-cream-muted uppercase md:text-[0.7rem]">Now calling</span>
            {letter ? (
              <span className="inline-flex h-8 min-w-8 items-center justify-center rounded-full text-sm font-black md:h-10 md:min-w-10 md:text-base" style={{ backgroundColor: COLUMN_COLORS[letter], color: COLUMN_INK[letter] }}>{letter}</span>
            ) : null}
            <span className="font-display text-2xl leading-none font-semibold tracking-tight text-cream tabular-nums md:text-4xl">{current}</span>
          </div>
        </div>
      ) : null}

      {phase === "calling" && current != null && !liveScene ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-[18%] z-[25] flex justify-center px-4">
          <div className="v2-callout text-center">
            <p className="mb-1 font-sans text-[0.7rem] tracking-[0.32em] text-cream-muted uppercase">Now calling</p>
            <p className="font-display text-6xl leading-none font-semibold tracking-tight text-cream drop-shadow-[0_12px_28px_rgba(0,0,0,0.7)] md:text-8xl">{letter}<span className="ml-3 tabular-nums">{current}</span></p>
          </div>
        </div>
      ) : null}

      {phase === "celebrating" && (
        <p className="pointer-events-none absolute inset-0 z-[36] flex items-center justify-center font-display text-[16vw] leading-none font-semibold tracking-tight text-cream drop-shadow-[0_8px_24px_rgba(0,0,0,0.45)] md:text-[8rem]">Bingo</p>
      )}
    </div>
  );
}
